/**
 * /inspections — listado con búsqueda y filtros.
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ClipboardCheck, Plus, Search } from 'lucide-react'
import { PageHeader } from '@/components/app-shell'
import { Card } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { StatusPill } from '@/components/ui/status-pill'
import { listInspections } from '@/lib/api'

const TYPES = [
  '',
  'Daily',
  'Weekly',
  'Monthly',
  'Pre-Trip',
  'Post-Trip',
  'Safety',
  'Maintenance',
  'Custom',
] as const
const STATUSES = ['', 'Passed', 'Passed With Issues', 'Failed'] as const

export function InspectionsPage() {
  const [q, setQ] = useState('')
  const [type, setType] = useState('')
  const [status, setStatus] = useState('')

  const query = useQuery({
    queryKey: ['inspections', q, type, status],
    queryFn: () =>
      listInspections({
        q: q || undefined,
        type: type || undefined,
        status: status || undefined,
      }),
  })

  const rows = query.data?.inspections ?? []

  return (
    <>
      <PageHeader
        title="Inspections"
        subtitle={`${rows.length} registro${rows.length === 1 ? '' : 's'}`}
        actions={
          <Link
            to="/inspections/new"
            className="inline-flex h-11 min-h-11 items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring lg:h-10 lg:min-h-10"
          >
            <Plus className="size-4" aria-hidden />
            New inspection
          </Link>
        }
      />

      <div className="mb-4 flex flex-col gap-3 sm:flex-row">
        <label className="relative min-w-0 flex-1">
          <span className="sr-only">Buscar</span>
          <Search
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Unidad, inspector, notas…"
            className="h-11 w-full rounded-xl border border-border bg-card pr-3 pl-10 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </label>
        <select
          aria-label="Tipo"
          value={type}
          onChange={(e) => setType(e.target.value)}
          className="h-11 rounded-xl border border-border bg-card px-3 text-sm"
        >
          <option value="">Todos los tipos</option>
          {TYPES.filter(Boolean).map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select
          aria-label="Resultado"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="h-11 rounded-xl border border-border bg-card px-3 text-sm"
        >
          <option value="">Todos los resultados</option>
          {STATUSES.filter(Boolean).map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {query.isLoading ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : rows.length === 0 ? (
        <Card>
          <EmptyState
            icon={ClipboardCheck}
            title="Sin inspecciones"
            description="Crea la primera o importa el histórico con npm run seed:excel."
            action={
              <Link to="/inspections/new" className="text-sm font-medium text-primary">
                Nueva inspección
              </Link>
            }
          />
        </Card>
      ) : (
        <Card className="divide-y divide-border overflow-hidden">
          {rows.map((row) => (
            <Link
              key={row.id}
              to="/inspections/$inspectionId"
              params={{ inspectionId: row.id }}
              className="flex min-h-[60px] items-center gap-3 px-4 py-3 transition-colors hover:bg-muted"
            >
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium">
                  Unit {row.unitNumber} · {row.inspectionType}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {new Date(row.inspectionDatetime).toLocaleString()}
                  {row.inspectorName ? ` · ${row.inspectorName}` : ''}
                </span>
              </span>
              <StatusPill status={row.overallStatus} />
            </Link>
          ))}
        </Card>
      )}
    </>
  )
}
